import React, { Component, useEffect } from 'react';
import { array, bool, func, number, object, string } from 'prop-types';
import { compose } from 'redux';
import { Form as FinalForm, FormSpy } from 'react-final-form';
import classNames from 'classnames';

import { FormattedMessage, intlShape, injectIntl } from '../../../util/reactIntl';
import { getDefaultTimeZoneOnBrowser, timestampToDate } from '../../../util/dates';
import { propTypes } from '../../../util/types';
import { BOOKING_PROCESS_NAME } from '../../../transactions/transaction';
import moment from 'moment';
import { FieldRadioButton, FieldSelect, Form, H6, PrimaryButton } from '../../../components';
import EstimatedCustomerBreakdownMaybe from '../EstimatedCustomerBreakdownMaybe';
import FieldDateAndTimeInput from './FieldDateAndTimeInput';

import css from './BookingTimeForm.module.css';
import { getServices, isInstantBooking } from '../../../util/dataExtractors';
import { isArrayLength, isLocaleGreek } from '../../../util/genericHelpers';
import { composeValidators, required } from '../../../util/validators';
import SetTimeSlot from '../SetTimeSlot';
import { capitalizeFirstLetter } from '../../../helpers/dataDupe';
import { selectIsAuthenticated } from '../../../ducks/auth.duck';
import { useSelector } from 'react-redux';
function flattenTimeSlots(inputObj) {
  return Object.values(inputObj).reduce((acc, obj) => {
    return acc.concat(obj.timeSlots);
  }, []);
}

export class BookingTimeFormComponent extends Component {
  constructor(props) {
    super(props);
    this.state = {
      activeSlot: null,
    };

    this.handleFormSubmit = this.handleFormSubmit.bind(this);
    this.handleOnChange = this.handleOnChange.bind(this);
    this.onSetActiveSlot = this.onSetActiveSlot.bind(this);
  }

  handleFormSubmit(e) {
    gtag('event', 'booking_attempt', { send_to: 'AW-11487056950/kGGWCKD-mZAZELaouuUq' });
    this.props.onSubmit(e);
  }

  onSetActiveSlot(slot) {
    this.setState({ activeSlot: slot });
  }
  // When the values of the form are updated we need to fetch
  // lineItems from this template's backend for the EstimatedTransactionMaybe
  // In case you add more fields to the form, make sure you add
  // the values here to the orderData object.
  handleOnChange(formValues) {
    const { bookingStartTime, bookingEndTime, duration = '15', services } = formValues.values;
    const startDate = bookingStartTime ? timestampToDate(bookingStartTime) : null;
    const endDate = bookingEndTime ? timestampToDate(bookingEndTime) : null;

    const listingId = this.props.listingId;
    const isOwnListing = this.props.isOwnListing;
    const timeZone = moment.tz.guess();

    // We expect values bookingStartTime and bookingEndTime to be strings
    // which is the default case when the value has been selected through the form
    const isSameTime = bookingStartTime === bookingEndTime;
    if (bookingStartTime && bookingEndTime && !isSameTime && !this.props.fetchLineItemsInProgress) {
      this.props.onFetchTransactionLineItems({
        orderData: {
          bookingStart: moment(startDate)
            .tz(timeZone)
            .toDate(),
          bookingEnd: moment(startDate)
            .tz(timeZone)
            .add(services?.duration, 'minutes')
            .toDate(),
          duration,
          services,
        },
        listingId,
        isOwnListing,
      });
    }
  }

  render() {
    const {
      rootClassName,
      className,
      price: unitPrice,
      dayCountAvailableForBooking,
      marketplaceName,
      ...rest
    } = this.props;
    const classes = classNames(rootClassName || css.root, className);

    return (
      <FinalForm
        {...rest}
        unitPrice={unitPrice}
        onSubmit={this.handleFormSubmit}
        render={fieldRenderProps => {
          const {
            endDatePlaceholder,
            startDatePlaceholder,
            form,
            pristine,
            handleSubmit,
            intl,
            isOwnListing,
            listingId,
            values,
            monthlyTimeSlots,
            onFetchTimeSlots,
            timeZone,
            lineItems,
            fetchLineItemsInProgress,
            fetchLineItemsError,
            currentListing = {},
            invalid,
            listing,
          } = fieldRenderProps;

          const startTime = values && values.bookingStartTime ? values.bookingStartTime : null;
          const endTime = values && values.bookingEndTime ? values.bookingEndTime : null;
          const startDate = startTime ? timestampToDate(startTime) : null;
          const endDate = endTime ? timestampToDate(endTime) : null;

          // This is the place to collect breakdown estimation data. See the
          // EstimatedCustomerBreakdownMaybe component to change the calculations
          // for customized payment processes.
          const breakdownData =
            startDate && endDate
              ? {
                  startDate,
                  endDate,
                }
              : null;

          const showEstimatedBreakdown =
            breakdownData && lineItems && !fetchLineItemsInProgress && !fetchLineItemsError;

          const hasDuration = (values && values.duration) || '15';
          const services = getServices(currentListing);

          const selectedService =
            values?.service > -1 && isArrayLength(services) ? services[values?.service] : '';

          const selectAvailableSlotsMessage = intl.formatMessage({
            id: 'BookingTimeForm.selectAvailableSlots',
          });

          const selectServiceMessage = intl.formatMessage({
            id: 'BookingTimeForm.selectServiceMessage',
          });

          const selectServiceMessageRequired = intl.formatMessage({
            id: 'BookingTimeForm.selectServiceMessageRequired',
          });

          const selectChatServiceMessageRequired = intl.formatMessage({
            id: 'BookingTimeForm.selectChatServiceMessageRequired',
          });

          const flattenedTimeSlots = monthlyTimeSlots && flattenTimeSlots(monthlyTimeSlots);
          const firstSlot = isArrayLength(flattenedTimeSlots) ? flattenedTimeSlots[0] : null;

          return (
            <Form onSubmit={handleSubmit} className={classes} enforcePagePreloadFor="CheckoutPage">
              <FormSpy
                subscription={{ values: true }}
                onChange={values => {
                  this.handleOnChange(values, timeZone);
                }}
              />
              <h3>{selectAvailableSlotsMessage}</h3>
              <SetTimeSlot firstSlot={firstSlot} values={values} form={form} />
              <div className={css.duration}>
                <p>{selectServiceMessage}</p>
                <div className={css.durationBtn}>
                  {isArrayLength(services) ? (
                    <FieldSelect
                      id="service"
                      name="service"
                      onChange={e => {
                        const selectedService =
                          e > -1 && isArrayLength(services) ? services[e] : '';
                        form.change('duration', selectedService?.duration);
                        form.change('services', selectedService);
                      }}
                      validate={composeValidators(required(selectServiceMessageRequired))}
                    >
                      <option value="" disabled>
                        {intl.formatMessage({
                          id: 'BookingTimeForm.service',
                        })}
                      </option>
                      {services.map((item, i) => (
                        <option key={i} value={i}>
                          {item.name} • €{item.price}
                        </option>
                      ))}
                    </FieldSelect>
                  ) : null}
                  <div className={css.chatOption}>
                    {isArrayLength(values?.services?.bookingOptions) &&
                    values?.services?.bookingOptions ? (
                      <FieldSelect
                        id="chatOption"
                        name="chatOption"
                        onChange={e => {
                          form.change(
                            'bookingType',
                            (values?.services?.bookingOptions &&
                              values?.services?.bookingOptions[e]) ||
                              'video-call'
                          );
                        }}
                        validate={composeValidators(required(selectChatServiceMessageRequired))}
                      >
                        <option value="" disabled>
                          {intl.formatMessage({
                            id: 'BookingTimeForm.selectBookingOption',
                          })}
                        </option>
                        {values?.services?.bookingOptions?.map((item, i) => (
                          <option key={i} value={i}>
                            {item ? capitalizeFirstLetter(item.split('-').join(' ')) : ''}
                          </option>
                        ))}
                      </FieldSelect>
                    ) : null}
                  </div>
                  {selectedService ? (
                    <Duration
                      duration={selectedService?.duration}
                      values={values}
                      form={form}
                      selectedService={selectedService}
                    ></Duration>
                  ) : null}
                </div>
              </div>
              <div className={css.timeSlotAndBreakdown}>
                {monthlyTimeSlots && timeZone ? (
                  <FieldDateAndTimeInput
                    startDateInputProps={{
                      label: intl.formatMessage({ id: 'BookingTimeForm.bookingStartTitle' }),
                      placeholderText: startDatePlaceholder,
                    }}
                    endDateInputProps={{
                      label: intl.formatMessage({ id: 'BookingTimeForm.bookingEndTitle' }),
                      placeholderText: endDatePlaceholder,
                    }}
                    isBookingDatePicker={true}
                    className={css.bookingDates}
                    listingId={listingId}
                    onFetchTimeSlots={onFetchTimeSlots}
                    monthlyTimeSlots={monthlyTimeSlots}
                    values={values}
                    intl={intl}
                    form={form}
                    pristine={pristine}
                    onSetActiveSlot={this.onSetActiveSlot}
                    activeSlot={this.state.activeSlot}
                    currentListing={currentListing}
                    timeZone={timeZone}
                    hasDuration={hasDuration}
                    dayCountAvailableForBooking={dayCountAvailableForBooking}
                  />
                ) : null}

                {showEstimatedBreakdown ? (
                  <div className={css.priceBreakdownContainer}>
                    {/* <H6 as="h3" className={css.bookingBreakdownTitle}>
                      <FormattedMessage id="BookingTimeForm.priceBreakdownTitle" />
                    </H6> */}
                    <hr className={css.totalDivider} />
                    <EstimatedCustomerBreakdownMaybe
                      breakdownData={breakdownData}
                      lineItems={lineItems}
                      timeZone={timeZone}
                      listing={listing}
                      selectedService={selectedService}
                      currency={unitPrice.currency}
                      marketplaceName={marketplaceName}
                      processName={BOOKING_PROCESS_NAME}
                    />
                  </div>
                ) : null}
              </div>

              {fetchLineItemsError ? (
                <span className={css.sideBarError}>
                  <FormattedMessage id="BookingTimeForm.fetchLineItemsError" />
                </span>
              ) : null}
              {isOwnListing ? null : (
                <div
                  className={css.submitButton}
                  style={{
                    width: '100%',
                    justifyContent: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}
                >
                  {invalid || !this.state.activeSlot ? (
                    <>
                      <LoginToBook />
                      <PrimaryButton
                        type="button"
                        className={css.disabledButton}
                        onClick={() => {
                          if (typeof window !== 'undefined') {
                            window.scrollTo(0, 0);
                          }
                        }}
                      >
                        <FormattedMessage
                          id={
                            isInstantBooking(currentListing)
                              ? 'BookingTimeForm.bookDirectly'
                              : 'BookingTimeForm.requestToBook'
                          }
                        />
                      </PrimaryButton>
                    </>
                  ) : (
                    <PrimaryButton type="submit" inProgress={fetchLineItemsInProgress}>
                      <FormattedMessage
                        id={
                          isInstantBooking(currentListing)
                            ? 'BookingTimeForm.bookDirectly'
                            : 'BookingTimeForm.requestToBook'
                        }
                      />
                    </PrimaryButton>
                  )}
                </div>
              )}

              <p className={css.finePrint}>
                <FormattedMessage id="BookingTimeForm.youWontBeChargedInfo" />
              </p>
            </Form>
          );
        }}
      />
    );
  }
}

function LoginToBook() {
  const isAuthenticated = useSelector(selectIsAuthenticated);
  return (
    <div>
      {isAuthenticated ? null : (
        <div style={{ marginBottom: '5px', fontSize: '10px', fontWeight: '600' }}>
          <FormattedMessage id="BookingTimeForm.loginToBook" />
        </div>
      )}
    </div>
  );
}

function Duration({ duration, values, form, selectedService }) {
  const isGreek = isLocaleGreek();
  return (
    <FieldRadioButton
      className={css.bookingRadiio}
      id={`${duration}Minutes`}
      name="duration"
      label={`${duration} ${isGreek ? 'λεπτά' : 'minutes'}`}
      value={duration}
    />
  );
}

BookingTimeFormComponent.defaultProps = {
  rootClassName: null,
  className: null,
  price: null,
  isOwnListing: false,
  listingId: null,
  startDatePlaceholder: null,
  endDatePlaceholder: null,
  monthlyTimeSlots: null,
  lineItems: null,
  fetchLineItemsError: null,
};

BookingTimeFormComponent.propTypes = {
  rootClassName: string,
  className: string,

  marketplaceName: string.isRequired,
  price: propTypes.money,
  isOwnListing: bool,
  listingId: propTypes.uuid,
  monthlyTimeSlots: object,
  onFetchTimeSlots: func.isRequired,
  timeZone: string.isRequired,

  onFetchTransactionLineItems: func.isRequired,
  lineItems: array,
  fetchLineItemsInProgress: bool.isRequired,
  fetchLineItemsError: propTypes.error,

  // from injectIntl
  intl: intlShape.isRequired,

  // for tests
  startDatePlaceholder: string,
  endDatePlaceholder: string,

  dayCountAvailableForBooking: number.isRequired,
};

const BookingTimeForm = compose(injectIntl)(BookingTimeFormComponent);
BookingTimeForm.displayName = 'BookingTimeForm';

export default BookingTimeForm;
